import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flame as FlameIcon, ShieldCheck as ShieldCheckIcon } from "@phosphor-icons/react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(email, password); nav("/"); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary relative overflow-hidden">
        <img src="https://customer-assets-agu9un31.emergentagent.net/job_gas-ops-manager/artifacts/5lsmlnz2_lucas%20photo.webp" alt="Lucas & Sons depot" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/85 to-primary/95" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative z-10 bg-white rounded-xl px-4 py-3 w-fit shadow-lg">
          <Logo size={44} showText />
        </div>
        <div className="relative z-10 text-white space-y-6">
          <h1 className="font-head text-4xl xl:text-5xl font-bold leading-tight">
            Butane & LPG<br />Distribution, <span className="text-red-400">Managed.</span>
          </h1>
          <p className="text-white/80 max-w-md">
            Enterprise operations platform for Lucas &amp; Sons — cylinders, deliveries, credit book, fleet & finance for San Juan, Belize.
          </p>
          <div className="flex gap-6 pt-4">
            <div className="flex items-center gap-2 text-white/90"><FlameIcon size={22} weight="duotone" /><span className="text-sm">Safety First</span></div>
            <div className="flex items-center gap-2 text-white/90"><ShieldCheckIcon size={22} weight="duotone" /><span className="text-sm">Compliance Ready</span></div>
          </div>
        </div>
        <div className="relative z-10 text-white/60 text-xs">© {new Date().getFullYear()} Lucas &amp; Sons Butane Gas Service</div>
      </div>

      {/* form */}
      <div className="flex items-center justify-center p-6 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6">
          <div className="lg:hidden flex justify-center"><Logo size={56} showText /></div>
          <div>
            <h2 className="font-head text-2xl font-bold">Welcome back</h2>
            <p className="text-muted-foreground text-sm">Sign in to your management console</p>
          </div>
          {err && <div data-testid="login-error" className="text-sm bg-destructive/10 text-destructive px-3 py-2 rounded-md">{err}</div>}
          <div className="space-y-2">
            <Label>Email or Driver Name</Label>
            <Input data-testid="login-email" type="text" autoComplete="off" placeholder="Enter your email or driver name" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>Password</Label>
            <Input data-testid="login-password" type="password" autoComplete="new-password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <Button data-testid="login-submit" type="submit" className="w-full" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
}
